import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateServiceFormValues, FieldErrors } from './types';
import { useClientHierarchy } from './useClientHierarchy';
import { useCreateServiceInteractions } from './useCreateServiceInteractions';
import { useCreateServiceOptions } from './useCreateServiceOptions';
import { useCreateServiceSubmit } from './useCreateServiceSubmit';
import { useLocationSearch } from './useLocationSearch';
import { usePrioritySelection } from './usePrioritySelection';
import { validateCreateServiceForm } from './validateForm';
import { ActionPermissions, CustomPermission } from '../../../@types';
import { CustomNodeAttributeType } from '../../../@types/service.type';
import { ServiceTypeMappingType } from '../../../@types/structure.type';
import { ServiceCreationRuleTypes } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { useAddService } from '../../../queries/service-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { useGetCurrentUserVisibility } from '../../../queries/visibilityRules-query';
import { resolveModelForPartType } from '../../../utils/reported-model';
import { isRuleExist } from '../../../utils/services';
import { toast } from '../../../utils/toast';

const EMPTY_VALUES: CreateServiceFormValues = {
  serviceTypeMapping: '',
  serviceTypeCode: '',
  actionTypeCode: '',
  processTypeCode: '',
  priorityCode: '',
  referenceNo1: '',
  referenceNo2: '',
  referenceNo3: '',
  reportedSerialNumber: '',
  reportedPartTypeName: '',
  reportedModel: '',
  mainFaultId: '',
  descriptionId: '',
  userDepotAllocation: '',
  scheduledAt: '',
  notes: '',
};

export const useCreateService = () => {
  const navigate = useNavigate();
  const addService = useAddService();
  const { data: permissions } = useGetUserSystemPermissions();
  const { data: visibility } = useGetCurrentUserVisibility();
  const isClientCustomerUser = !!visibility?.hasClientCustomer;

  const [values, setValues] = useState<CreateServiceFormValues>(EMPTY_VALUES);
  const [showOverride, setShowOverride] = useState(false);

  const hierarchy = useClientHierarchy();
  const locationSearch = useLocationSearch();

  const { serviceTypeOptions, attributeOptions, rawModelAttributes, mappingsLoading } =
    useCreateServiceOptions({
      parentId: hierarchy.selection.parentId,
      clientId: hierarchy.selection.clientId,
      contractId: hierarchy.selection.contractId,
      serviceTypeCode: values.serviceTypeCode,
      actionTypeCode: values.actionTypeCode,
    });

  const hasAllocationPermission = useMemo(
    () =>
      isUserHasPermission(
        ActionPermissions.Service_Edit_Allocation_Primary,
        (permissions ?? null) as CustomPermission[] | null
      ),
    [permissions]
  );

  const hasCreatePermission = useMemo(
    () =>
      isUserHasPermission(
        ActionPermissions.Service_Create,
        (permissions ?? null) as CustomPermission[] | null
      ),
    [permissions]
  );

  useEffect(() => {
    if (locationSearch.state.locationId) {
      hierarchy.syncFromLocationId(locationSearch.state.locationId);
    }
  }, [locationSearch.state.locationId]);

  // String-coerce both sides: SelectMenu/MUI preserves the numeric `value`
  // produced by serviceTypeMappings(), so values.serviceTypeMapping can hold a
  // number while m.id?.toString() returns a string — strict === would miss.
  const selectedMapping = useMemo<CustomNodeAttributeType | undefined>(
    () =>
      serviceTypeOptions.mappings.find((m) => String(m.id) === String(values.serviceTypeMapping)),
    [serviceTypeOptions.mappings, values.serviceTypeMapping]
  );

  const { selectedRules } = usePrioritySelection({
    priorities: attributeOptions.priorities,
    initialPriorityCode: attributeOptions.initialPriorityCode,
    priorityCode: values.priorityCode,
    setValues,
  });

  const descriptionOptions = values.mainFaultId
    ? (attributeOptions.descriptionsByFault[values.mainFaultId] ?? [])
    : [];
  const descriptionRequired =
    !!values.mainFaultId && !!attributeOptions.faultHasDescriptions[values.mainFaultId];
  const submittedFaultId = descriptionRequired ? values.descriptionId : values.mainFaultId;

  const updateValues = useCallback((patch: Partial<CreateServiceFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const onMappingChange = useCallback(
    (mappingId: string) => {
      const key = String(mappingId);
      const mapping = serviceTypeOptions.mappings.find((m) => String(m.id) === key);
      const v = mapping?.value as ServiceTypeMappingType | undefined;
      updateValues({
        serviceTypeMapping: key,
        serviceTypeCode: v?.selectedServiceType ?? '',
        actionTypeCode: v?.selectedActionType ?? '',
        processTypeCode: v?.selectedProcessType ?? '',
        mainFaultId: '',
        descriptionId: '',
      });
      setShowOverride(false);
    },
    [serviceTypeOptions.mappings, updateValues]
  );

  // If the client has exactly one available mapping, auto-select it the first
  // time the list resolves so the user doesn't have to open a single-item
  // dropdown. Skip when override is in use or a mapping is already chosen.
  useEffect(() => {
    if (showOverride) return;
    if (values.serviceTypeMapping) return;
    if (serviceTypeOptions.mappings.length !== 1) return;
    const only = serviceTypeOptions.mappings[0];
    if (only?.id == null) return;
    onMappingChange(String(only.id));
  }, [serviceTypeOptions.mappings, values.serviceTypeMapping, showOverride, onMappingChange]);

  // When entering override mode while a mapping is selected, explicitly carry
  // over the resolved service/action/process codes so the override fields
  // start from the mapping's values rather than blank.
  const onShowOverride = useCallback(
    (next: boolean) => {
      if (next && selectedMapping) {
        const v = selectedMapping.value as ServiceTypeMappingType | undefined;
        updateValues({
          serviceTypeCode: v?.selectedServiceType ?? '',
          actionTypeCode: v?.selectedActionType ?? '',
          processTypeCode: v?.selectedProcessType ?? '',
        });
      }
      setShowOverride(next);
    },
    [selectedMapping, updateValues]
  );

  useEffect(() => {
    if (
      values.mainFaultId &&
      !attributeOptions.mainFaultOptions.some((o) => o.value === values.mainFaultId)
    ) {
      updateValues({ mainFaultId: '', descriptionId: '' });
    }
  }, [attributeOptions.mainFaultOptions]);

  useEffect(() => {
    if (values.descriptionId && !descriptionOptions.some((o) => o.value === values.descriptionId)) {
      updateValues({ descriptionId: '' });
    }
  }, [descriptionOptions]);

  // Client/customer users get reportedModel auto-derived from the chosen part
  // type. Re-derive when the model attributes refetch (e.g. hierarchy change)
  // so a stale model id from a prior contract can't ride through into the
  // submit payload. Skipping for non-client users — they pick the model
  // explicitly via the Model dropdown.
  useEffect(() => {
    if (!isClientCustomerUser) return;
    const matched = resolveModelForPartType(rawModelAttributes, values.reportedPartTypeId);
    const next = matched ? String(matched.id) : '';
    if (next !== values.reportedModel) updateValues({ reportedModel: next });
  }, [isClientCustomerUser, rawModelAttributes, values.reportedPartTypeId]);

  const errors = useMemo<FieldErrors>(
    () =>
      validateCreateServiceForm({
        values,
        hierarchy: hierarchy.selection,
        location: locationSearch.state,
        showOverride,
        selectedRules,
        submittedFaultId,
        isClientCustomerUser,
      }),
    [
      values,
      hierarchy.selection,
      locationSearch.state,
      showOverride,
      selectedRules,
      submittedFaultId,
      isClientCustomerUser,
    ]
  );
  const isFormValid = Object.keys(errors).length === 0;

  // Reset the right-panel interactions if the user changes inputs that
  // determine which forms apply (priority and mapping drive the rule lookup).
  const interactions = useCreateServiceInteractions({
    permissions: (permissions ?? []) as CustomPermission[],
    resetTrigger: `${values.priorityCode}::${values.serviceTypeMapping}`,
  });

  const onSuccess = useCallback(() => toast.success('Service created'), []);
  const submit = useCreateServiceSubmit({
    values,
    hierarchy: hierarchy.selection,
    location: locationSearch.state,
    submittedFaultId,
    isFormValid,
    addService,
    navigate,
    interactions,
    onSuccess,
  });

  return {
    values,
    updateValues,
    onMappingChange,
    showOverride,
    setShowOverride: onShowOverride,
    hierarchy,
    locationSearch,
    serviceTypeOptions,
    attributeOptions,
    rawModelAttributes,
    mappingsLoading,
    selectedMapping,
    descriptionOptions,
    descriptionRequired,
    reference1Required: isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1),
    reference2Required: isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2),
    reference3Required: isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3),
    selectedRules,
    errors,
    validate: true,
    isFormValid,
    onSubmit: submit.onSubmit,
    isSubmitting: addService.isPending,
    submitError: submit.submitError,
    hasAllocationPermission,
    hasCreatePermission,
    isClientCustomerUser,
    permissionsLoaded: !!permissions && !!visibility,
    interactions,
    serviceProxy: submit.serviceProxy,
  };
};
